import { useQuery } from '@apollo/client/react'
import { Badge, Box, Center, HoverCard, Image, Portal, Spinner, Text, VStack } from '@chakra-ui/react'
import { gql } from '@src/lib/gql'
import React, { ReactNode, useState } from 'react'

/** All RAG source-type keys currently emitted by startups3 adapters. */
export type RagSourceTypeKey =
  'cms-article' | 'cms-podcast-episode' | 'cms-video' | 'app-playbook' | 'app-feature'

export type RagContentKind = 'article' | 'podcast' | 'video' | 'playbook' | 'feature'

export type RagContentRef = {
  kind: RagContentKind
  slug: string
}

const STARTUPS_HOST_RE = /(^|\.)startups\.com$/i

/** Map sco-citation source-type keys to content kinds used by the hover preview. */
export function kindFromSourceType(sourceType?: string | null): RagContentKind | null {
  switch (sourceType) {
    case 'cms-article':
      return 'article'
    case 'cms-podcast-episode':
      return 'podcast'
    case 'cms-video':
      return 'video'
    case 'app-playbook':
      return 'playbook'
    case 'app-feature':
      return 'feature'
    default:
      return null
  }
}

export function typeLabelForSourceType(sourceType?: string | null): string {
  switch (sourceType) {
    case 'cms-article':
      return 'Article'
    case 'cms-podcast-episode':
      return 'Podcast'
    case 'cms-video':
      return 'Video'
    case 'app-playbook':
      return 'Playbook'
    case 'app-feature':
      return 'Product'
    default:
      return sourceType ? sourceType.replace(/^(cms|app)-/, '').replace(/-/g, ' ') : 'Source'
  }
}

function typeLabelForKind(kind: RagContentKind): string {
  switch (kind) {
    case 'article':
      return 'Article'
    case 'podcast':
      return 'Podcast'
    case 'video':
      return 'Video'
    case 'playbook':
      return 'Playbook'
    case 'feature':
      return 'Product'
    default:
      return 'Source'
  }
}

/**
 * Parse a citation / markdown href into a content ref we can look up.
 * Supports absolute startups.com URLs and relative site paths used by RAG adapters.
 */
export function parseRagContentRef(href?: string | null, sourceType?: string | null): RagContentRef | null {
  if (!href) {
    // No URL — still allow feature/playbook previews keyed only by source-type when slug unknown.
    return null
  }

  let pathname = href
  try {
    if (/^https?:\/\//i.test(href)) {
      const url = new URL(href)
      if (!STARTUPS_HOST_RE.test(url.hostname)) {
        return null
      }
      pathname = url.pathname
    } else if (href.startsWith('//')) {
      const url = new URL(`https:${href}`)
      if (!STARTUPS_HOST_RE.test(url.hostname)) {
        return null
      }
      pathname = url.pathname
    } else if (!href.startsWith('/')) {
      return null
    }
  } catch {
    return null
  }

  // Strip trailing slash and query/hash leftovers
  const path = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/'

  const articleMatch = path.match(/^\/articles\/([^/]+)$/)
  if (articleMatch) {
    return { kind: 'article', slug: decodeURIComponent(articleMatch[1]) }
  }

  const podcastMatch = path.match(/^\/podcast\/([^/]+)$/)
  if (podcastMatch) {
    return { kind: 'podcast', slug: decodeURIComponent(podcastMatch[1]) }
  }

  // /videos/{listSlug}/{videoSlug} or /videos/{videoSlug}
  const videoNested = path.match(/^\/videos\/[^/]+\/([^/]+)$/)
  if (videoNested) {
    return { kind: 'video', slug: decodeURIComponent(videoNested[1]) }
  }

  const videoSingle = path.match(/^\/videos\/([^/]+)$/)
  if (videoSingle) {
    return { kind: 'video', slug: decodeURIComponent(videoSingle[1]) }
  }

  // /playbooks/{slug} or /playbooks/{slug}/{section}
  const playbookMatch = path.match(/^\/playbooks\/([^/]+)/)
  if (playbookMatch) {
    return { kind: 'playbook', slug: decodeURIComponent(playbookMatch[1]) }
  }

  // App features use product paths (/members/matching, /tools, /advisors, …).
  // Prefer explicit source-type; otherwise treat unknown app paths as features when tagged.
  const kindFromType = kindFromSourceType(sourceType)
  if (kindFromType === 'feature') {
    return { kind: 'feature', slug: path }
  }
  if (kindFromType === 'playbook') {
    const segments = path.split('/').filter(Boolean)
    const last = segments[segments.length - 1]
    if (last) {
      return { kind: 'playbook', slug: decodeURIComponent(last) }
    }
  }

  // Fallback: source-type known but path didn't match standard patterns — still try last segment
  if (kindFromType === 'article' || kindFromType === 'podcast' || kindFromType === 'video') {
    const segments = path.split('/').filter(Boolean)
    const last = segments[segments.length - 1]
    if (last) {
      return { kind: kindFromType, slug: decodeURIComponent(last) }
    }
  }

  return null
}

type PreviewData = {
  title?: string | null
  imageUrl?: string | null
  snippet?: string | null
  typeLabel: string
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | null {
  for (const v of values) {
    if (v && String(v).trim()) {
      return String(v).trim()
    }
  }
  return null
}

const ARTICLE_PREVIEW_QUERY = gql`
  query RagSourceArticlePreview($slug: String!) {
    article(where: { slug: $slug }, stage: PUBLISHED) {
      id
      title
      teaser
      seoDescription
      synopsis
      tldr
      featuredImage {
        id
        url
      }
    }
  }
`

const PODCAST_PREVIEW_QUERY = gql`
  query RagSourcePodcastPreview($slug: String!) {
    podcastEpisode(where: { slug: $slug }, stage: PUBLISHED) {
      id
      title
      description
      seoDescription
      posterImage {
        id
        url
      }
      seoImage {
        id
        url
      }
    }
  }
`

const VIDEO_PREVIEW_QUERY = gql`
  query RagSourceVideoPreview($slug: String!) {
    video(where: { slug: $slug }, stage: PUBLISHED) {
      id
      title
      shortDescription
      longDescription
      externalImageUrl
      featuredImage {
        id
        url
      }
    }
  }
`

const PLAYBOOK_PREVIEW_QUERY = gql`
  query RagSourcePlaybookPreview($slug: String!) {
    scoPlaybook(slug: $slug) {
      id
      title
      teaser
      tldr
      illustration
      seoDescription
    }
  }
`

function useRagSourcePreview(
  ref: RagContentRef | null,
  enabled: boolean
): {
  loading: boolean
  data: PreviewData | null
} {
  const articleQuery = useQuery(ARTICLE_PREVIEW_QUERY, {
    variables: { slug: ref?.slug },
    skip: !enabled || !ref || ref.kind !== 'article',
    ssr: false,
  })

  const podcastQuery = useQuery(PODCAST_PREVIEW_QUERY, {
    variables: { slug: ref?.slug },
    skip: !enabled || !ref || ref.kind !== 'podcast',
    ssr: false,
  })

  const videoQuery = useQuery(VIDEO_PREVIEW_QUERY, {
    variables: { slug: ref?.slug },
    skip: !enabled || !ref || ref.kind !== 'video',
    ssr: false,
  })

  const playbookQuery = useQuery(PLAYBOOK_PREVIEW_QUERY, {
    variables: { slug: ref?.slug },
    skip: !enabled || !ref || ref.kind !== 'playbook',
    ssr: false,
  })

  if (!ref) {
    return { loading: false, data: null }
  }

  if (ref.kind === 'article') {
    const article = articleQuery.data?.article
    return {
      loading: articleQuery.loading,
      data: article
        ? {
            title: article.title,
            imageUrl: article.featuredImage?.url,
            snippet: firstNonEmpty(article.teaser, article.seoDescription, article.synopsis, article.tldr),
            typeLabel: 'Article',
          }
        : null,
    }
  }

  if (ref.kind === 'podcast') {
    const episode = podcastQuery.data?.podcastEpisode
    return {
      loading: podcastQuery.loading,
      data: episode
        ? {
            title: episode.title,
            imageUrl: episode.posterImage?.url || episode.seoImage?.url,
            snippet: firstNonEmpty(episode.description, episode.seoDescription),
            typeLabel: 'Podcast',
          }
        : null,
    }
  }

  if (ref.kind === 'video') {
    const video = videoQuery.data?.video
    return {
      loading: videoQuery.loading,
      data: video
        ? {
            title: video.title,
            imageUrl: video.featuredImage?.url || video.externalImageUrl,
            snippet: firstNonEmpty(video.shortDescription, video.longDescription),
            typeLabel: 'Video',
          }
        : null,
    }
  }

  if (ref.kind === 'playbook') {
    const playbook = playbookQuery.data?.scoPlaybook
    return {
      loading: playbookQuery.loading,
      data: playbook
        ? {
            title: playbook.title,
            imageUrl: playbook.illustration || null,
            snippet: firstNonEmpty(playbook.teaser, playbook.tldr, playbook.seoDescription),
            typeLabel: 'Playbook',
          }
        : null,
    }
  }

  // app-feature: static catalog on the server — no GraphQL preview; use citation fallbacks.
  if (ref.kind === 'feature') {
    return {
      loading: false,
      data: {
        title: null,
        imageUrl: null,
        snippet: null,
        typeLabel: 'Product',
      },
    }
  }

  return { loading: false, data: null }
}

type RagSourcePreviewBodyProps = {
  contentRef: RagContentRef
  /** Fallback title when CMS lookup is still loading or fails */
  fallbackTitle?: string
  fallbackSnippet?: string
  fallbackImageUrl?: string
}

const RagSourcePreviewBody = (props: RagSourcePreviewBodyProps) => {
  const { loading, data } = useRagSourcePreview(props.contentRef, true)
  const title = data?.title || props.fallbackTitle || 'Source'
  const snippet = data?.snippet || props.fallbackSnippet
  const imageUrl = data?.imageUrl || props.fallbackImageUrl
  const typeLabel = data?.typeLabel || typeLabelForKind(props.contentRef.kind)

  return (
    <VStack align={'stretch'} gap={2} maxW={'280px'}>
      {imageUrl ? (
        <Box bg={'bg.muted'} borderRadius={'md'} overflow={'hidden'}>
          <Image alt={title} display={'block'} maxH={'140px'} objectFit={'cover'} src={imageUrl} w={'full'} />
        </Box>
      ) : loading ? (
        <Center bg={'bg.muted'} borderRadius={'md'} minH={'80px'}>
          <Spinner size={'sm'} />
        </Center>
      ) : null}

      <VStack align={'stretch'} gap={1}>
        <Badge colorPalette={'blue'} size={'sm'} variant={'subtle'} w={'fit-content'}>
          {typeLabel}
        </Badge>
        <Text fontSize={'sm'} fontWeight={'semibold'} lineClamp={2}>
          {title}
        </Text>
        {snippet ? (
          <Text color={'fg.muted'} fontSize={'xs'} lineClamp={3}>
            {snippet}
          </Text>
        ) : loading && !data?.title ? (
          <Center py={1}>
            <Spinner size={'xs'} />
          </Center>
        ) : null}
      </VStack>
    </VStack>
  )
}

export type RagSourceHoverCardProps = {
  href?: string | null
  sourceType?: string | null
  fallbackTitle?: string
  fallbackSnippet?: string
  fallbackImageUrl?: string
  children: ReactNode
  /** When false, render children without hover card */
  enabled?: boolean
}

/**
 * Chakra V3 hover-card wrapper for linked RAG sources.
 * Looks up featured image + title + teaser/description by content type when available;
 * falls back to citation attributes for app-feature and when CMS is unavailable.
 */
export const RagSourceHoverCard = (props: RagSourceHoverCardProps) => {
  const contentRef = parseRagContentRef(props.href, props.sourceType)
  const [open, setOpen] = useState(false)

  // Always offer a popover when we can identify a RAG type (including feature fallbacks).
  const effectiveRef =
    contentRef ||
    (kindFromSourceType(props.sourceType)
      ? ({
          kind: kindFromSourceType(props.sourceType)!,
          slug: props.href || props.fallbackTitle || 'source',
        } as RagContentRef)
      : null)

  if (props.enabled === false || !effectiveRef) {
    return <>{props.children}</>
  }

  return (
    <HoverCard.Root
      closeDelay={150}
      open={open}
      openDelay={250}
      positioning={{ placement: 'top', gutter: 8 }}
      onOpenChange={details => setOpen(details.open)}
    >
      <HoverCard.Trigger asChild>{props.children}</HoverCard.Trigger>
      <Portal>
        <HoverCard.Positioner>
          <HoverCard.Content
            borderColor={'border'}
            borderRadius={'xl'}
            borderWidth={'1px'}
            boxShadow={'lg'}
            maxW={'300px'}
            p={3}
          >
            {open ? (
              <RagSourcePreviewBody
                contentRef={effectiveRef}
                fallbackImageUrl={props.fallbackImageUrl}
                fallbackSnippet={props.fallbackSnippet}
                fallbackTitle={props.fallbackTitle}
              />
            ) : null}
          </HoverCard.Content>
        </HoverCard.Positioner>
      </Portal>
    </HoverCard.Root>
  )
}

export default RagSourceHoverCard
